import React from "react";
import { Button, Card, CardBody, CardHeader, CardTitle, Col, Row } from "reactstrap";

import Joi from "joi-browser";

import BaseForm from "../common/BaseForm";
import { addOrUpdateVideoTune } from "../../services/storeService";
import { getGlobalStreamConfigsFull } from "../../services/globalStreamConfigService";
import { toast } from "react-toastify";
import ResponsiveRow from "./base/ResponsiveRow";
import FormDataContext from "../shared/contexts/FormDataContext";
import FieldBuilder from "../shared/utils/FieldBuilder";

// Fallback defaults if API call fails
const FALLBACK_DEFAULTS = {
  acceleration: 2.0,
  constant_rate: 28,
  fps: 5,
};

class VideoTuneForm extends BaseForm {
  state = {
    data: {},
    errors: {},
    defaults: { ...FALLBACK_DEFAULTS },
  };

  schema = {
    acceleration: Joi.number().min(1).max(2.5).default(FALLBACK_DEFAULTS.acceleration).label("Acceleration"),
    constant_rate: Joi.number().min(15).max(51).default(FALLBACK_DEFAULTS.constant_rate).label("Constant Rate"),
    fps: Joi.number().min(5).max(15).default(FALLBACK_DEFAULTS.fps).label("FPS"),
  };

  async componentDidMount() {
    this.mounted = true;
    const { videoTune, store } = this.props;
    let data = {};
    if (videoTune) {
      data = { ...videoTune };
    } else {
      data.store = store.id;
    }

    // Fetch constant_rate default from API (cached), other defaults are hardcoded
    try {
      const { data: globalConfig } = await getGlobalStreamConfigsFull();
      const defaults = {
        ...FALLBACK_DEFAULTS,
        constant_rate: globalConfig.alert_constant_rate_default_factor ?? FALLBACK_DEFAULTS.constant_rate,
      };
      if (this.mounted) this.setState({ data, defaults });
    } catch {
      if (this.mounted) this.setState({ data });
    }
  }

  componentWillUnmount() {
    this.mounted = false;
  }

  save = async () => {
    this.props.store.video_tune = { ...this.state.data };
    try {
      await addOrUpdateVideoTune(this.props.store);
      this.props.onSaveVideoTune(this.props.store.video_tune);
      toast.info("Store config updated.");
    } catch (ex) {
      if (ex.response) {
        if (ex.response.status === 400) {
          Object.keys(ex.response.data).forEach(function (key) {
            const message = `${key}: ${ex.response.data[key][0]}`;
            toast.error(message);
          });
        } else if (ex.response.status === 404) {
          toast.error("Store not found");
        } else {
          toast.error("Failed to update");
        }
      } else {
        toast.error("Failed to update");
      }
    }
  };

  render() {
    const { data, defaults } = this.state;

    return (
      <Card className="video-tune">
        <CardHeader>
          <CardTitle tag="h4">Video Tune</CardTitle>
        </CardHeader>
        <CardBody>
          <FormDataContext.Provider value={data}>
            <ResponsiveRow
              md="4"
              fields={[
                new FieldBuilder()
                  .setId("constant_rate")
                  .setName("constant_rate")
                  .setComponent(
                    <>
                      {this.renderInput("constant_rate", "Compression", "number")}
                      <label>default : {defaults.constant_rate}</label>
                    </>
                  )
                  .build(),
                new FieldBuilder()
                  .setId("fps")
                  .setName("fps")
                  .setComponent(
                    <>
                      {this.renderInput("fps", "FPS", "number")}
                      <label>default : {defaults.fps}</label>
                    </>
                  )
                  .build(),
                new FieldBuilder()
                  .setId("acceleration")
                  .setName("acceleration")
                  .setComponent(
                    <>
                      {this.renderInput("acceleration", "Acceleration", "number")}
                      <label>default : {defaults.acceleration.toFixed(2)}</label>
                    </>
                  )
                  .build(),
              ]}
              bottomSeparator={false}
            />
          </FormDataContext.Provider>

          <Row>
            <Col>
              <Button className="float-right btn-success m-2" onClick={this.save}>
                <i className="fas fa-save mr-2" />
                Save
              </Button>
            </Col>
          </Row>
          <hr
            style={{
              borderColor: "grey",
              height: 0.5,
            }}
          />
          <p className="text-warning">
            Before you use these settings : please read the notion page (link
            <a
              href="https://www.notion.so/Veesion-Manager-afde3f7062104791b96063887a146057#1ff16e5d54c0804789e7d1b2bf12e95d"
              target="_blank"
              rel="noopener noreferrer"
            >
              &nbsp;here
            </a>
            ), which explains in which context they should be modified, and in which context the modification won&apos;t
            have impact
          </p>
        </CardBody>
      </Card>
    );
  }
}

export default VideoTuneForm;
